/**
 * @fileoverview Post-Confirmation page
 * This page collects additional professional information after initial confirmation,
 * including file upload functionality for credentials.
 *
 * @module PostConfirmation
 */

import { useState, useEffect } from "react"
import { Col, Row, message, Form, Spin, Upload, Button } from "antd"
import { UploadOutlined } from "@ant-design/icons"
import { useNavigate } from "react-router-dom"
import samu from "../assets/images/postconf.webp"
import "../assets/styles/LoginPage.css"
import "../assets/styles/PreConfirmacion.css"
import { useTranslation } from "react-i18next"
import { getMyUser, updateUserProfileFinish } from "../services/AuthServices"
import { useAuth } from "../contexts/authContext"
import FloatingInput from "../components/FloatingInput"
import FloatingSelect from "../components/FloatingSelect"
import { fileToBase64 } from "../utils/filesBase64/fileToBase64"

/**
 * PostConfirmation page that collects professional information and credentials
 *
 * @returns {JSX.Element} PostConfirmation page
 */
const PostConfirmation = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const [signature, setSignature] = useState(null)
  const [loading, setLoading] = useState(false)
  const [checkingStatus, setCheckingStatus] = useState(true)
  const { getToken } = useAuth()

  const prefixOptions = [
    { value: "dr", label: "Dr." },
    { value: "dra", label: "Dra." },
    { value: "mv", label: "M.V." },
    { value: "other", label: t("postConfirmation.other") || "Other" },
  ]

  /**
   * Check user's veterinary and active status on component mount
   */
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        setCheckingStatus(true)
        const token = await getToken()

        if (!token) {
          navigate("/login")
          return
        }

        const userData = await getMyUser(token)

        if (!userData || !userData.veterinary) {
          navigate("/preconfirmacion")
          return
        }

        if (!userData.active) {
          navigate("/espera")
          return
        }

        if(userData.reportCustomization.displayName && userData.reportCustomization.license && userData.reportCustomization.signature && userData.phoneNumber) {
          navigate("/dashboard")
          return
        }
      } catch (error) {
        console.error("Error checking user status:", error)
      } finally {
        setCheckingStatus(false)
      }
    }

    checkUserStatus()
  }, [getToken, navigate])

  /**
   * Handles the continue button click, navigating to the waiting page
   */
  const handleContinue = async () => {
    try {
      await form.validateFields()

      const values = form.getFieldsValue()
      const { name, license, phone_number, prefix } = values

      setLoading(true)

      const token = await getToken()
      if (!token) {
        navigate("/login")
        return
      }

      const displayName = `${prefix} ${name}`.trim()

      const profileData = {
        phoneNumber: phone_number,
        displayName: displayName,
        license: license,
        signature: signature,
      }

      await updateUserProfileFinish(token, profileData)

      message.success(t("postConfirmation.success") || "Information saved successfully")
      navigate("/dashboard")
    } catch (error) {
      if (error.errorFields) {
        return
      }

      console.error("Error saving information:", error)
      message.error(t("postConfirmation.error") || "An error occurred")
    } finally {
      setLoading(false)
    }
  }

  

  /**
   * Handles signature file upload
   * @param {Object} info - Upload info object
   */
  const handleSignatureUpload = async (info) => {
    if (info.file.status === "uploading") {
      return
    }

    if (info.file.status === "done") {
      const file = info.file.originFileObj

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        message.error(t("postConfirmation.fileError.sizeLimit") || "The file exceeds the maximum size of 2MB.")
        return
      }

      try {
        const base64Signature = await fileToBase64(file)
        setSignature(base64Signature)
        message.success(`${file.name} uploaded successfully`)
      } catch (error) {
        console.error("Error processing signature:", error)
        message.error(t("postConfirmation.fileProcessError") || "Error processing file")
      }
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} upload failed.`)
    }
  }

  const customRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok")
    }, 0)
  }

  return (
    <div className="body">
      {checkingStatus ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : (
        <Row className="content">
          <Col lg={10} sm={24} xs={24} className="newleftside2">
            <div>
              <img src={samu || "/placeholder.svg"} alt="ovejero" className="dogimage2" />
            </div>
          </Col>
          <Col lg={14} sm={24} xs={24} className="rightside">
            <Col span={24}>
              <Col span={24} className="rightside-container7">
                <div className="container">
                  <p className="bluetitle decrease left">{t("postConfirmation.almostDone")}</p>
                  <p className="subtitle3">{t("postConfirmation.needMoreInfo")}</p>

                  <Form form={form} layout="vertical" className="veterinary-form">
                    <Form.Item
                      name="phone_number"
                      rules={[
                        { required: true, message: t("postConfirmation.phoneRequired") || "Phone number is required" },
                        { min: 5, message: t("postConfirmation.phoneMinLength") || "Phone number is too short" },
                        { max: 20, message: t("postConfirmation.phoneMaxLength") || "Phone number is too long" },
                      ]}
                    >
                      <FloatingInput label={t("postConfirmation.phoneNumber")} required={true} />
                    </Form.Item>

                    <Form.Item
                      name="prefix"
                      rules={[
                        { required: true, message: t("postConfirmation.prefixRequired") || "Prefix is required" },
                      ]}
                    >
                      <FloatingSelect
                        label={t("postConfirmation.prefix")}
                        options={prefixOptions}
                        fullWidth
                        required={true}
                      />
                    </Form.Item>

                    <Form.Item
                      name="name"
                      rules={[
                        { required: true, message: t("postConfirmation.nameRequired") || "Name is required" },
                        {
                          min: 1,
                          max: 100,
                          message: t("postConfirmation.nameLength") || "Name must be between 1-100 characters",
                        },
                      ]}
                    >
                      <FloatingInput label={t("postConfirmation.name")} required={true} />
                    </Form.Item>

                    {/* Signature upload field */}
                    <Form.Item
                      label={t("postConfirmation.uploadSignature") || "Upload Signature"}
                      className="form-item-spacing"
                    >
                      <Upload
                        name="signature"
                        listType="picture"
                        className="logo-uploader"
                        showUploadList={true}
                        beforeUpload={(file) => {
                          const isImage = file.type.startsWith("image/")
                          if (!isImage) {
                            message.error(t("postConfirmation.imageOnly") || "You can only upload image files!")
                          }
                          return isImage || Upload.LIST_IGNORE
                        }}
                        customRequest={customRequest}
                        onChange={handleSignatureUpload}
                        maxCount={1}
                      >
                        <Button icon={<UploadOutlined />}>
                          {t("postConfirmation.uploadSignature") || "Upload Signature"}
                        </Button>
                      </Upload>
                    </Form.Item>

                    <Form.Item
                      name="license"
                      rules={[
                        {
                          required: true,
                          message: t("postConfirmation.licenseRequired") || "License number is required",
                        },
                      ]}
                    >
                      <FloatingInput label={t("postConfirmation.license")} required={true} />
                    </Form.Item>

                    <Form.Item className="button-container">
                      <button className="button9" onClick={handleContinue} disabled={loading} type="button">
                        {loading ? (
                          <span>
                            <Spin size="small" style={{ marginRight: "8px" }} />{" "}
                            {t("postConfirmation.processing") || "Processing..."}
                          </span>
                        ) : (
                          t("postConfirmation.continue")
                        )}
                      </button>
                    </Form.Item>
                  </Form>
                </div>
              </Col>
            </Col>
          </Col>
        </Row>
      )}
    </div>
  )
}

export default PostConfirmation
